import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";
import { Box, Collapse, Typography } from "@mui/material";

import { HEAT_MAP_SPECTRUM } from "../../utils/HeatMapUtils";
import {
  selectHeatMapHighColor,
  selectHeatMapLowColor,
} from "../../store/heatmap/HeatMapSelectors";
import {
  setHeatMapHighColor,
  setHeatMapLowColor,
} from "../../store/heatmap/HeatMapActions";
import {
  StyledConfigSection,
  StyledSwatchButton,
  StyledSwatchPalette,
  StyledPaletteSwatch,
  heatMapModalStyles
} from "./HeatMapModalStyles";

type SwatchTarget = "high" | "low" | null;

/**
 * Customize gradient — 2 swatch boxes (highest, lowest)
 *
 * Clicking a swatch reveals the 8-color HEAT_MAP_SPECTRUM palette in a single
 * row of 25×25 cells.
 */
const HeatMapColorGradient: React.FC = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const styles = heatMapModalStyles();
  const highColor = useSelector(selectHeatMapHighColor);
  const lowColor = useSelector(selectHeatMapLowColor);

  const [expanded, setExpanded] = useState<SwatchTarget>(null);

  const togglePalette = (target: Exclude<SwatchTarget, null>) => {
    setExpanded((prev) => (prev === target ? null : target));
  };

  const handleColorPick = (color: string) => {
    if (expanded === "high") dispatch(setHeatMapHighColor(color));
    else if (expanded === "low") dispatch(setHeatMapLowColor(color));
  };

  return (
    <StyledConfigSection>
      <Typography variant="heading" className="heatmap-section-label">
        {intl.formatMessage({
          id: "heatMap.gradientLabel",
          defaultMessage: "Customize Gradient",
        })}
      </Typography>

      <Box sx={styles.colorBox}>
        <Box sx={styles.valueBox}>
          <Typography variant="subtitle2" sx={styles.valueLabel}>
            {intl.formatMessage({
              id: "heatMap.highestValue",
              defaultMessage: "Highest Value:",
            })}
          </Typography>
          <StyledSwatchButton
            type="button"
            swatchColor={highColor}
            isActive={expanded === "high"}
            onClick={() => togglePalette("high")}
            aria-label="Pick color for highest value"
            aria-expanded={expanded === "high"}
          />
        </Box>

        <Box sx={styles.valueBox}>
          <Typography variant="subtitle2" sx={styles.valueLabel}>
            {intl.formatMessage({
              id: "heatMap.lowestValue",
              defaultMessage: "Lowest Value:",
            })}
          </Typography>
          <StyledSwatchButton
            type="button"
            swatchColor={lowColor}
            isActive={expanded === "low"}
            onClick={() => togglePalette("low")}
            aria-label="Pick color for lowest value"
            aria-expanded={expanded === "low"}
          />
        </Box>
      </Box>

      <Collapse in={expanded !== null} unmountOnExit>
        <StyledSwatchPalette>
          {HEAT_MAP_SPECTRUM.map((color) => {
            const isSelected =
              (expanded === "high" && color === highColor) ||
              (expanded === "low" && color === lowColor);
            return (
              <StyledPaletteSwatch
                key={color}
                type="button"
                swatchColor={color}
                isSelected={isSelected}
                onClick={() => handleColorPick(color)}
                aria-label={`Color ${color}`}
              />
            );
          })}
        </StyledSwatchPalette>
      </Collapse>
    </StyledConfigSection>
  );
};

export default HeatMapColorGradient;
